import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { TryItNowWidget } from '@/components/TryItNowWidget';
import {
  Sparkles, CheckCircle2, ArrowRight, BookOpen, Lightbulb, AlertTriangle, Crown,
} from 'lucide-react';

const steps = [
  { title: '1. Open with a strong hook', desc: 'Lead with the single most desirable feature — view, location, light, layout. Skip "Welcome home" clichés.' },
  { title: '2. Paint the lifestyle', desc: 'Show how the buyer will use the space: morning coffee on the terrace, dinner parties in the open kitchen, working from the bright office.' },
  { title: '3. Hit the must-have facts', desc: 'Bedrooms, bathrooms, square meters/feet, parking, outdoor space, year built — buyers filter on these.' },
  { title: '4. List standout features in scannable form', desc: 'Use short bullets for renovations, smart home, energy class, premium appliances. Buyers skim.' },
  { title: '5. Close with location & next step', desc: 'Mention nearby schools, transit, beach, or town center, then a clear CTA: book a viewing, call, or visit the microsite.' },
];

const dos = [
  'Use sensory, specific words ("sun-drenched terrace", not "nice outdoor area")',
  'Mention recent upgrades with the year ("new kitchen, 2024")',
  'Match length to platform — 80 words for Instagram, 200+ for premium portals',
  'End with a clear call to action',
];
const donts = [
  'Avoid clichés: "must see", "won\'t last", "diamond in the rough"',
  "Don't oversell flaws — buyers will see them in person",
  "Don't write a wall of text — short paragraphs and bullets convert better",
  "Don't forget keywords buyers search for (neighborhood, type, condition)",
];

const faqs = [
  {
    q: 'How long should a real estate listing description be?',
    a: 'It depends on the platform. Instagram captions: 50–100 words. Facebook posts: 100–200 words. Premium portal listings (Zillow, Idealista, Rightmove): 200–400 words. ListBright generates the right length for each channel automatically.',
  },
  {
    q: 'What should I include in a property listing description?',
    a: 'A strong opening hook, lifestyle framing, key facts (beds/baths/size), standout features, location highlights, and a clear call to action. Use short paragraphs and bullets so buyers can skim.',
  },
  {
    q: 'Can AI write my real estate listing description?',
    a: 'Yes — and it can do it in seconds. ListBright analyzes your photos, generates 5 description formats (Facebook, Instagram, Stories, Marketplace, Premium) in parallel, and translates each into 16 languages.',
  },
  {
    q: 'What words should I avoid in listing descriptions?',
    a: 'Skip vague clichés ("cozy", "must see", "won\'t last"), fair-housing-restricted terms (avoid family/religion/race references in the US), and anything you can\'t prove. Be specific and factual.',
  },
  {
    q: 'How do I write a listing description that sells fast?',
    a: 'Lead with the strongest feature, paint a lifestyle picture, list facts clearly, and close with a clear next step. Pair the text with enhanced photos — listings with great photos AND descriptions sell ~2x faster.',
  },
];

export default function HowToWriteListingDescription() {
  const navigate = useNavigate();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to Write a Real Estate Listing Description',
      description: 'Step-by-step guide to writing a real estate listing description that sells, with do\'s, don\'ts, and an AI tool to do it for you.',
      step: steps.map((s, i) => ({
        '@type': 'HowToStep', position: i + 1, name: s.title, text: s.desc,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="How to Write a Real Estate Listing Description (2026 Guide)"
        description="Step-by-step guide to writing real estate listing descriptions that sell. Includes do's, don'ts, examples, and a free AI tool to write yours in seconds."
        canonical="https://listbright.ai/how-to-write-a-real-estate-listing-description"
        breadcrumbs={[{ label: 'How to Write a Listing Description', path: '/how-to-write-a-real-estate-listing-description' }]}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container max-w-4xl mx-auto px-4 relative text-center space-y-6">
          <Badge variant="outline" className="px-4 py-1.5 text-sm">
            <BookOpen className="w-3.5 h-3.5 mr-1.5" /> 2026 Guide
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
            How to Write a <span className="text-primary">Listing Description</span> That Sells
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Five-step framework used by top agents — plus a free AI tool that writes it for you in
            seconds, in 16 languages.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Button asChild size="lg"><a href="#try">Generate Mine Free <ArrowRight className="w-4 h-4 ml-2" /></a></Button>
            <Button asChild size="lg" variant="outline"><a href="#guide">Read the Guide</a></Button>
          </div>
        </div>
      </section>

      <section id="guide" className="py-20">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">The 5-Step Framework</h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Follow this order. It mirrors how buyers actually read listings.
          </p>
          <div className="space-y-4">
            {steps.map((s) => (
              <Card key={s.title} className="border-border/50">
                <CardContent className="p-6 space-y-2">
                  <h3 className="font-semibold text-lg">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-lg">Do</h3>
                </div>
                <ul className="space-y-2">
                  {dos.map((d) => (
                    <li key={d} className="flex gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-muted-foreground" />
                  <h3 className="font-semibold text-lg">Don't</h3>
                </div>
                <ul className="space-y-2">
                  {donts.map((d) => (
                    <li key={d} className="flex gap-2 text-sm text-muted-foreground">
                      <AlertTriangle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section id="try" className="py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="mb-3"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Free AI Tool</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Or Just Let AI Write It</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Upload a photo. Get a polished description in 5 formats and 16 languages — in seconds.
            </p>
          </div>
          <TryItNowWidget onGetStarted={() => navigate('/auth')} />
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container max-w-4xl mx-auto px-4 text-center space-y-6">
          <Badge className="mb-2"><Crown className="w-3.5 h-3.5 mr-1.5" /> The Complete Kit</Badge>
          <h2 className="text-3xl md:text-4xl font-bold">Description + Photos + Video + Microsite</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            A great description is only half the listing. ListBright also enhances your photos with
            10 rules, builds a slideshow video, and publishes a shareable microsite — all in 10 minutes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Button asChild size="lg">
              <Link to="/single-property-kit">
                Single Property Kit – $15 <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pricing">See All Plans</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <Card key={f.q}>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{f.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg"><a href="#try">Generate Mine Free <ArrowRight className="w-4 h-4 ml-2" /></a></Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/ai-real-estate-descriptions"><Lightbulb className="w-4 h-4 mr-2" /> More on AI Descriptions</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}